import React, { useEffect, useMemo, useState } from "react";

import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";

import {
  useCreateCredential,
  useUpdateCredentialById,
} from "src/agents/queries/credentials";
import { useAgentModels } from "src/agents/queries/management";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import type { Option } from "src/base-components/Select";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import type { CredentialItem } from "src/clients/agent-management";
import { IconButton } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { ModelIcon } from "src/genai/ModelSelector";
import { logger } from "src/utils/logger";

type CredentialProvider = "openai" | "google_vertex" | "anthropic";

const CREDENTIAL_PROVIDERS: readonly CredentialProvider[] = [
  "openai",
  "google_vertex",
  "anthropic",
] as const;

const isCredentialProvider = (
  provider: string,
): provider is CredentialProvider =>
  (CREDENTIAL_PROVIDERS as readonly string[]).includes(provider);

type OpenAIRegion = "global" | "us" | "eu";

const OPENAI_REGION_TO_BASE_URL: Record<OpenAIRegion, string> = {
  global: "https://api.openai.com/v1",
  us: "https://us.api.openai.com/v1",
  eu: "https://eu.api.openai.com/v1",
};

const baseUrlToRegion = (baseUrl: string | undefined): OpenAIRegion => {
  const match = Object.entries(OPENAI_REGION_TO_BASE_URL).find(
    ([, url]) => url === baseUrl,
  );
  return (match?.[0] as OpenAIRegion) ?? "global";
};

type CredentialInputs = {
  provider: CredentialProvider;
  name: string;
  models: string[];
  // OpenAI fields
  apiKey?: string;
  region?: OpenAIRegion;
  // Vertex AI fields
  serviceAccountJson?: string;
  projectId?: string;
  location?: string;
  // Anthropic fields
  anthropicApiKey?: string;
};

type Props = {
  open: boolean;
  credential?: CredentialItem;
  onClose: () => void;
};

const PROVIDER_LABELS: Partial<Record<CredentialProvider, string>> = {
  openai: "OpenAI",
  google_vertex: "Google Vertex AI",
  anthropic: "Anthropic",
};

const NAME_PLACEHOLDERS: Record<CredentialProvider, string> = {
  openai: "e.g. Production OpenAI",
  google_vertex: "e.g. Production Vertex AI",
  anthropic: "e.g. Production Anthropic",
};

const ProviderOptionLabel: React.FC<{ provider: CredentialProvider }> = ({
  provider,
}) => (
  <div className="flex items-center gap-2">
    <ModelIcon
      modelName={PROVIDER_LABELS[provider] ?? provider}
      provider={provider}
    />
    <span>{PROVIDER_LABELS[provider] ?? provider}</span>
  </div>
);

/**
 * Build the secret data payload for credential creation based on provider.
 * React Hook Form's required rules prevent submission with empty fields.
 */
const buildSecretData = (
  input: CredentialInputs,
): Record<string, string | undefined> => {
  switch (input.provider) {
    case "openai":
      return {
        api_key: input.apiKey,
        base_url: OPENAI_REGION_TO_BASE_URL[input.region ?? "global"],
      };

    case "google_vertex":
      return {
        service_account_json: input.serviceAccountJson,
        project_id: input.projectId || undefined,
        location: input.location || undefined,
      };

    case "anthropic":
      return {
        api_key: input.anthropicApiKey,
      };
  }
};

const PROVIDER_OPTIONS: Option[] = CREDENTIAL_PROVIDERS.map((provider) => ({
  key: provider,
  value: <ProviderOptionLabel provider={provider} />,
  searchText: PROVIDER_LABELS[provider],
}));

const OPENAI_REGION_OPTIONS: Option[] = [
  { key: "global", value: "Global" },
  { key: "us", value: "United States" },
  { key: "eu", value: "Europe" },
];

export const UpsertCredentialModal: React.FC<Props> = ({
  open,
  credential,
  onClose,
}) => {
  const createCredential = useCreateCredential();
  const updateCredentialById = useUpdateCredentialById();
  const modelsQuery = useAgentModels();

  const isEditing = credential !== undefined;

  const [secretsEditable, setSecretsEditable] = useState<
    Record<string, boolean>
  >({});

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CredentialInputs>({
    mode: "onChange",
    defaultValues: {
      provider:
        credential?.provider && isCredentialProvider(credential.provider)
          ? credential.provider
          : "openai",
      name: credential?.name ?? "",
      models: credential?.models ?? [],
    },
  });

  const selectedProvider = watch("provider");

  // Filter available models by the selected provider
  const providerModels = useMemo(() => {
    if (!modelsQuery.data) return [];
    return modelsQuery.data.filter(
      (m) => m.provider === selectedProvider && m.credential_id == null,
    );
  }, [modelsQuery.data, selectedProvider]);

  // Reset name and models when switching providers on create
  useEffect(() => {
    if (!isEditing) {
      setValue("name", "");
      setValue("models", []);
    }
  }, [selectedProvider, isEditing, setValue]);

  const enableSecret = (fieldName: keyof CredentialInputs) => {
    setSecretsEditable((prev) => ({ ...prev, [fieldName]: true }));
    setValue(fieldName, "", { shouldDirty: true });
  };

  const modelOptions: Option[] = useMemo(
    () =>
      providerModels.map((model) => ({
        key: model.model_id,
        value: model.display_name,
      })),
    [providerModels],
  );

  const REQUIRED_SECRETS: Record<
    CredentialProvider,
    (keyof CredentialInputs)[]
  > = {
    openai: ["apiKey"],
    google_vertex: ["serviceAccountJson"],
    anthropic: ["anthropicApiKey"],
  };

  // When editing: allow submit if no secrets are being changed (metadata-only update)
  // or if all required secrets have been re-entered
  const hasSecretsBeingEdited = REQUIRED_SECRETS[selectedProvider].some(
    (field) => secretsEditable[field],
  );
  const hasAllRequiredSecrets =
    !isEditing ||
    !hasSecretsBeingEdited ||
    REQUIRED_SECRETS[selectedProvider].every((field) => secretsEditable[field]);

  // Reset form when credential changes, populating non-secret config fields
  useEffect(() => {
    setSecretsEditable({});
    if (credential) {
      const provider = isCredentialProvider(credential.provider)
        ? credential.provider
        : "openai";
      reset({
        provider,
        name: credential.name,
        models: credential.models ?? [],
        region: baseUrlToRegion(credential.config?.base_url),
        projectId: credential.config?.project_id ?? undefined,
        location: credential.config?.location ?? undefined,
      });
    } else {
      reset({
        provider: "openai",
        name: "",
        models: [],
      });
    }
  }, [credential, reset]);

  const submitCredential = handleSubmit(async (input: CredentialInputs) => {
    try {
      if (isEditing) {
        const hasSecretChanges = REQUIRED_SECRETS[input.provider].some(
          (field) => secretsEditable[field],
        );

        if (hasSecretChanges) {
          const secretData = buildSecretData(input);
          await updateCredentialById.mutateAsync({
            credentialId: credential.id,
            data: {
              name: input.name,
              models: input.models,
              credential: secretData,
            },
          });
        } else {
          await updateCredentialById.mutateAsync({
            credentialId: credential.id,
            data: {
              name: input.name,
              models: input.models,
            },
          });
        }
      } else {
        const secretData = buildSecretData(input);
        await createCredential.mutateAsync({
          provider: input.provider,
          data: {
            ...secretData,
            name: input.name,
            models: input.models,
          },
        });
      }

      toastActions.success({
        title: isEditing ? "Credential updated" : "Credential created",
        description: `${PROVIDER_LABELS[input.provider]} credential saved successfully.`,
      });
      onClose();
    } catch (error) {
      logger.error(`Error when saving credential: ${error}`);

      let description =
        "An error occurred while saving the credential. Please try again.";
      if (isAxiosError(error) && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          description = detail
            .map((item: unknown) =>
              typeof item === "object" && item !== null && "msg" in item
                ? (item as { msg: string }).msg
                : String(item),
            )
            .join(". ");
        } else {
          description = String(detail);
        }
      }

      toastActions.failure({
        title: "Failed to save credential",
        description,
      });
    }
  });

  const renderModelAssignment = () => {
    if (modelOptions.length === 0) return null;

    return (
      <FormItem gap="sm" label="Models" isRequired>
        {errors.models?.message && (
          <ErrorHint>{errors.models.message}</ErrorHint>
        )}
        <Controller
          control={control}
          name="models"
          shouldUnregister
          rules={{
            validate: (v) =>
              (v && v.length > 0) || "At least one model must be selected",
          }}
          render={({ field }) => (
            <Select
              options={modelOptions}
              placeholder="Select models this key can access"
              value={field.value ?? []}
              multiple
              showSelectAllAsOption
              onChange={(values) => field.onChange(values)}
            />
          )}
        />
      </FormItem>
    );
  };

  const renderProviderFields = () => {
    switch (selectedProvider) {
      case "openai":
        return (
          <>
            <FormItem gap="sm" label="API Key" isRequired>
              {errors.apiKey?.message && (
                <ErrorHint>{errors.apiKey.message}</ErrorHint>
              )}
              <div className="flex w-full items-center gap-x-2">
                <div className="flex-1">
                  <Input
                    disabled={isEditing && !secretsEditable.apiKey}
                    placeholder={
                      isEditing && !secretsEditable.apiKey
                        ? "••••••••"
                        : "sk-..."
                    }
                    type="password"
                    fullWidth
                    {...register("apiKey", {
                      required:
                        !isEditing || secretsEditable.apiKey
                          ? "API key is required"
                          : false,
                      validate: {
                        format: (v) =>
                          !v ||
                          v.startsWith("sk-") ||
                          "API key must start with 'sk-'",
                      },
                    })}
                  />
                </div>
                {isEditing && !secretsEditable.apiKey && (
                  <Tooltip placement="right" title="Edit API key" asChild>
                    <IconButton
                      icon={faEdit}
                      size="xs"
                      onClick={() => enableSecret("apiKey")}
                    />
                  </Tooltip>
                )}
              </div>
            </FormItem>
            <FormItem gap="sm" label="Region">
              <Controller
                control={control}
                name="region"
                render={({ field }) => (
                  <Select
                    options={OPENAI_REGION_OPTIONS}
                    value={field.value ?? "global"}
                    onChange={(value) => {
                      field.onChange(value as OpenAIRegion);
                      if (isEditing && !secretsEditable.apiKey) {
                        enableSecret("apiKey");
                      }
                    }}
                  />
                )}
              />
            </FormItem>
          </>
        );

      case "google_vertex":
        return (
          <>
            <FormItem gap="sm" label="Service Account JSON" isRequired>
              {errors.serviceAccountJson?.message && (
                <ErrorHint>{errors.serviceAccountJson.message}</ErrorHint>
              )}
              <div className="flex w-full items-center gap-x-2">
                <div className="flex-1">
                  {isEditing && !secretsEditable.serviceAccountJson ? (
                    <Input
                      placeholder="••••••••"
                      type="password"
                      disabled
                      fullWidth
                    />
                  ) : (
                    <Controller
                      control={control}
                      name="serviceAccountJson"
                      render={({ field }) => (
                        <Textarea
                          placeholder='{"type": "service_account", ...}'
                          rows={6}
                          value={field.value ?? ""}
                          fullWidth
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                      rules={{
                        required:
                          !isEditing || secretsEditable.serviceAccountJson
                            ? "Service account JSON is required"
                            : false,
                        validate: {
                          json: (v) => {
                            if (!v) return true;
                            try {
                              JSON.parse(v);
                              return true;
                            } catch {
                              return "Invalid JSON format";
                            }
                          },
                          fields: (v) => {
                            if (!v) return true;
                            try {
                              const data = JSON.parse(v);
                              const required = [
                                "type",
                                "project_id",
                                "private_key",
                                "client_email",
                              ];
                              const missing = required.filter(
                                (f) => !(f in data),
                              );
                              if (missing.length > 0) {
                                return `Missing required fields: ${missing.join(", ")}`;
                              }
                              return true;
                            } catch {
                              return true; // JSON parse error handled by json validator
                            }
                          },
                          type: (v) => {
                            if (!v) return true;
                            try {
                              const data = JSON.parse(v);
                              if (
                                data.type !== undefined &&
                                data.type !== "service_account"
                              ) {
                                return "Type must be 'service_account'";
                              }
                              return true;
                            } catch {
                              return true;
                            }
                          },
                        },
                      }}
                    />
                  )}
                </div>
                {isEditing && !secretsEditable.serviceAccountJson && (
                  <Tooltip
                    placement="right"
                    title="Edit service account JSON"
                    asChild
                  >
                    <IconButton
                      icon={faEdit}
                      size="xs"
                      onClick={() => enableSecret("serviceAccountJson")}
                    />
                  </Tooltip>
                )}
              </div>
            </FormItem>
            <FormItem
              gap="sm"
              label="Project ID (optional, will be read from JSON if not provided)"
            >
              <Input
                placeholder="my-gcp-project"
                fullWidth
                {...register("projectId")}
              />
            </FormItem>
            <FormItem gap="sm" label="Location (optional)">
              <Input
                placeholder="us-central1"
                fullWidth
                {...register("location")}
              />
            </FormItem>
          </>
        );

      case "anthropic":
        return (
          <FormItem gap="sm" label="API Key" isRequired>
            {errors.anthropicApiKey?.message && (
              <ErrorHint>{errors.anthropicApiKey.message}</ErrorHint>
            )}
            <div className="flex w-full items-center gap-x-2">
              <div className="flex-1">
                <Input
                  disabled={isEditing && !secretsEditable.anthropicApiKey}
                  placeholder={
                    isEditing && !secretsEditable.anthropicApiKey
                      ? "••••••••"
                      : "sk-ant-..."
                  }
                  type="password"
                  fullWidth
                  {...register("anthropicApiKey", {
                    required:
                      !isEditing || secretsEditable.anthropicApiKey
                        ? "API key is required"
                        : false,
                    validate: {
                      format: (v) =>
                        !v ||
                        v.startsWith("sk-ant-") ||
                        "API key must start with 'sk-ant-'",
                    },
                  })}
                />
              </div>
              {isEditing && !secretsEditable.anthropicApiKey && (
                <Tooltip placement="right" title="Edit API key" asChild>
                  <IconButton
                    icon={faEdit}
                    size="xs"
                    onClick={() => enableSecret("anthropicApiKey")}
                  />
                </Tooltip>
              )}
            </div>
          </FormItem>
        );

      default:
        return null;
    }
  };

  return (
    <Modal afterLeave={() => reset()} open={open} size="md" onClose={onClose}>
      <Modal.Header>
        {isEditing ? "Update AI credential" : "Add AI credential"}
      </Modal.Header>
      <form onSubmit={submitCredential}>
        <Modal.Content>
          <FormItem gap="sm" label="Provider">
            <Controller
              control={control}
              name="provider"
              render={({ field }) => (
                <Select
                  disabled={isEditing}
                  options={PROVIDER_OPTIONS}
                  placeholder="Select a provider"
                  value={field.value}
                  onChange={(value) =>
                    field.onChange(value as CredentialProvider)
                  }
                />
              )}
            />
          </FormItem>

          <FormItem gap="sm" label="Name" isRequired>
            {errors.name?.message && (
              <ErrorHint>{errors.name.message}</ErrorHint>
            )}
            <Input
              placeholder={NAME_PLACEHOLDERS[selectedProvider]}
              fullWidth
              {...register("name", {
                required: "Name is required",
                maxLength: {
                  value: 255,
                  message: "Name must be 255 characters or less",
                },
              })}
            />
          </FormItem>

          {renderProviderFields()}

          {renderModelAssignment()}
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="save-credential-button"
              disabled={!isDirty || isSubmitting || !hasAllRequiredSecrets}
              htmlType="submit"
              loading={isSubmitting}
              variant="primary"
            >
              {isEditing ? "Update" : "Create"}
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
